/**
 * Upload des documents de vérification d'identité.
 *
 * Le endpoint proxy backend documenté (`POST /api/shared/documents/upload`, JWT + clé secrète
 * ajoutée côté serveur) n'est toujours pas implémenté (404). En attendant, et par choix explicite
 * (décision prise avec l'équipe produit malgré le risque), on appelle le bucket directement via
 * `uploadFileToBucket` (même pattern que la signature pro et les pièces jointes de consultation) :
 * la clé secrète maîtresse du bucket (`VITE_BUCKET_SECRET_KEY`) est donc exposée dans le bundle
 * frontend. À remplacer par le vrai proxy dès qu'il sera disponible côté backend.
 */

import { uploadFileToBucket } from "./bucket";

const ALLOWED_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "webp"];
const MAX_SIZE_BYTES = 50 * 1024 * 1024; // 50 Mo — limite imposée par le bucket (upload-file).

export type UploadDocumentResult = { ok: true; url: string } | { ok: false; message: string };

function fileExtension(filename: string): string {
  const match = /\.([a-z0-9]+)$/i.exec(filename.trim());
  return match ? match[1].toLowerCase() : "";
}

export function validateIdentityDocumentFile(file: File): { ok: true } | { ok: false; message: string } {
  const ext = fileExtension(file.name);
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    return { ok: false, message: "Format non accepté, seuls PDF/JPG/PNG/WEBP sont autorisés." };
  }
  if (file.size > MAX_SIZE_BYTES) {
    return { ok: false, message: "Fichier trop volumineux (50 Mo maximum)." };
  }
  return { ok: true };
}

export async function uploadIdentityDocument(
  file: File,
  folderName: string,
  /** Nom suggéré sans extension, ex. "carte_cip_1072408380" — le bucket ajoute l'extension d'origine. */
  filename?: string
): Promise<UploadDocumentResult> {
  const validation = validateIdentityDocumentFile(file);
  if (!validation.ok) return validation;

  const uploaded = await uploadFileToBucket(file, folderName, filename);
  if (!uploaded.ok) return { ok: false, message: uploaded.message };
  return { ok: true, url: uploaded.url };
}
